import { api } from './api';

export type DocumentType = 'license' | 'certification' | 'insurance' | 'other';

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp'
];

export const validateFile = (file: File): { valid: boolean; error?: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Only PDF and image files (JPEG, PNG, WebP) are allowed' };
  }

  return { valid: true };
};

export const uploadDocument = async (
  file: File,
  userId: string,
  documentType: DocumentType
): Promise<UploadResult> => {
  try {
    const validation = validateFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const result = await api.uploadFile('/documents/upload', file, {
      user_id: userId,
      document_type: documentType,
    });

    return { success: true, url: result.url };
  } catch (error: any) {
    console.error('Upload error:', error);
    return { 
      success: false, 
      error: error?.message || 'An unexpected error occurred' 
    };
  }
};

export const deleteDocument = async (url: string): Promise<boolean> => {
  try {
    // Extract document ID from URL or use the full URL
    // Assuming the API accepts the document URL or ID
    await api.delete(`/documents?url=${encodeURIComponent(url)}`);
    return true;
  } catch (error) {
    console.error('Delete error:', error);
    return false;
  }
};

export const getDocumentPreviewUrl = (url: string): string => {
  return url;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const getExpirationStatus = (expiryDate: string): {
  status: 'active' | 'expiring' | 'expired';
  daysRemaining: number;
  message: string;
} => {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) {
    return {
      status: 'expired',
      daysRemaining,
      message: `Expired ${Math.abs(daysRemaining)} days ago`
    };
  }

  if (daysRemaining <= 30) {
    return {
      status: 'expiring',
      daysRemaining,
      message: `Expires in ${daysRemaining} days`
    };
  }

  return {
    status: 'active',
    daysRemaining,
    message: `Valid for ${daysRemaining} days`
  };
};
